/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */
import {
    EnvironmentalDefaults,
    ISettingGroupDefinition,
    SettingGroupDefinition
} from "@nuance/hierarchical-settings-ui-domain";
import { ILogger, getLogger } from "@nuance/hux-diagnostics";
import { CmdBuilder, IViewModel } from "@nuance/hap-components";
import { IAdvancedViewViewModel } from "../AdvancedViewViewModels";

const logger: ILogger = getLogger();

export interface IEnvironmentViewProps {
    definition: ISettingGroupDefinition;
    advancedViewVM: IAdvancedViewViewModel;
}

export interface IEnvPanelData {
    settingName: string;
    environment: string;
    defaultValue: string;
    isNew: boolean;
    originalEnvironment: string;
}

export interface IEnvPanelProps {
    definition: ISettingGroupDefinition;
    data: IEnvPanelData;
    panelTitle: string;
    isPanelOpen: boolean;
    onClosePanel?: () => void;
    onSaveContent?: (data: IEnvPanelData) => void;
}

export interface IEnvironmentViewState {
    currentIndex: number; // current row index for edit
    selectedRows: number[];
    definition: ISettingGroupDefinition;
    currentPanelProps?: IEnvPanelProps;
}

export class EnvironmentSettingCommands {
    constructor() {
        const cb = new CmdBuilder();
        const cmds = {
            onSelectionChanged: cb.bindCmd(this.selectionChangedHandler),
            onAdd: cb.bindCmdNP(this.addHandler),
            onEdit: cb.bindCmdNP(this.editHandler),
            onSave: cb.bindCmd(this.saveDataHandler),
            onCancel: cb.bindCmdNP(this.cancelHandler),
            onDelete: cb.bindCmdNP(this.deleteHandler)
        };

        this.Cmds = cmds;
        this.Reducer = cb.buildReducer<IEnvironmentViewState>(cmds);
    }

    readonly Cmds;
    readonly Reducer;

    /////////////////////////////////////////////////////////////////
    // Command handlers
    /////////////////////////////////////////////////////////////////

    selectionChangedHandler = (state: IEnvironmentViewState, selectedRows: number[]) => {
        state.selectedRows = selectedRows;
        state.currentIndex = selectedRows[0];
        return state;
    };

    addHandler = (state: IEnvironmentViewState): IEnvironmentViewState => {
        logger.trackEvent("Add environment setting");

        const panelProps: IEnvPanelProps = {
            data: {
                isNew: true,
                settingName: state.definition.Fields[0]?.SettingName,
                environment: "",
                originalEnvironment: "",
                defaultValue: state.definition.Fields[0]?.DefaultValue.toString()
            },

            definition: state.definition,
            panelTitle: "Add New Environment",
            isPanelOpen: true
        };

        state.currentPanelProps = panelProps;
        return state;
    };

    editHandler = (state: IEnvironmentViewState): IEnvironmentViewState => {
        if (state.currentIndex !== -1) {
            const currentEnv = state.definition.EnvironmentalDefaultsOverride[state.currentIndex];

            if (currentEnv === undefined) {
                logger.logError("Unable to find environment default", {
                    index: state.currentIndex.toString()
                });
                throw new Error("Unable to find environment default.");
            }
            logger.trackEvent("Edit environment setting", {
                name: currentEnv.SettingName,
                env: currentEnv.Environment
            });

            const panelProps: IEnvPanelProps = {
                data: {
                    isNew: false,
                    settingName: currentEnv.SettingName,
                    environment: currentEnv.Environment,
                    originalEnvironment: currentEnv.Environment,
                    defaultValue: currentEnv.DefaultValue
                },

                definition: state.definition,
                panelTitle: "Edit Environment Defaults",
                isPanelOpen: true
            };

            state.currentPanelProps = panelProps;
        } else {
            logger.logWarning("Attempting to edit environment setting but index is -1");
        }
        return state;
    };

    deleteHandler = (state: IEnvironmentViewState): IEnvironmentViewState => {
        if (state.selectedRows.length > 0) {
            const rows = new Set(state.selectedRows);
            logger.trackEvent(`Delete ${rows.size} environment default(s)`);
            const envWithValuesRemoved = state.definition.EnvironmentalDefaultsOverride.filter(
                (_, index) => !rows.has(index)
            );
            state.definition.EnvironmentalDefaultsOverride = envWithValuesRemoved;
            state.currentIndex = -1;
            state.selectedRows = [];
            state.currentPanelProps = undefined;
        } else {
            logger.logWarning("Attempting to delete environment setting but nothing selected");
        }

        return state;
    };

    saveDataHandler = (state: IEnvironmentViewState, data: IEnvPanelData) => {
        logger.trackEvent("Save environment setting", {
            name: data.settingName,
            env: data.environment
        });

        const sg = new SettingGroupDefinition(state.definition);
        if (data.isNew) {
            //add
            const envOverride = new EnvironmentalDefaults(data.settingName);
            envOverride.DefaultValue = data.defaultValue;
            envOverride.Environment = data.environment;
            sg.AddEnvironmentalDefault(envOverride);
        } else {
            //edit
            const envOverride = sg.EnvironmentalDefaultsOverride[state.currentIndex];
            if (envOverride === undefined) {
                logger.logError("Unable to find environment default", {
                    settingName: data.settingName,
                    environment: data.environment
                });
                throw new Error("Unable to find environment default.");
            }
            envOverride.DefaultValue = data.defaultValue;
            envOverride.Environment = data.environment;
        }

        state.definition = sg.toISettingGroupDefinition();
        if (state.currentPanelProps) state.currentPanelProps.isPanelOpen = false;
        state.currentPanelProps = undefined;

        return state;
    };

    cancelHandler = (state: IEnvironmentViewState): IEnvironmentViewState => {
        logger.trackEvent("Cancel editing environment setting");
        state.currentPanelProps = undefined;
        return state;
    };
}

type IEnvironmentSettingCmds = EnvironmentSettingCommands["Cmds"];

export type IEnvironmentViewModel = IViewModel<IEnvironmentViewState, IEnvironmentSettingCmds>;
